import { Component, computed, effect, inject, signal } from "@angular/core"
import { httpResource } from "@angular/common/http"
import { Router } from "@angular/router"
import { toSignal } from "@angular/core/rxjs-interop"
import { catchError, of } from "rxjs"
import { ToastService } from "src/app/services"
import { Category, CategoryTypes, Column, PagedBenefit, TableLazyLoadEvent } from "../../models"
import { BenefitsService, CategoryService } from "../../services"
import { ColumnFilter, toPagedParams } from "../../helpers/TablePaging"
import { TableComponent } from "../../components/table/table.component"

@Component({
    selector: "valben-benefits-table",
    templateUrl: "./benefits-table.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [TableComponent],
})
export class BenefitsTablePage {
    private _benefitsService = inject(BenefitsService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    cols: Column[] = [
        { field: "names", subField: "hr", header: "Naslov", bodyType: "langs", filterType: "text", filterMode: "contains", pairWith: "descriptions" },
        { field: "descriptions", subField: "hr", header: "Opis", bodyType: "langs", filterType: "text", filterMode: "contains" },
        { field: "createdByUser", header: "Kreiranje", bodyType: "text", filterType: "text", filterMode: "contains", pairWith: "modifiedByUser" },
        { field: "modifiedByUser", header: "Zadnja izmjena", bodyType: "text", filterType: "text", filterMode: "contains", prefix: "↳" },
        { field: "createdDate", header: "Datum kreiranja", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "modifiedDate" },
        { field: "modifiedDate", header: "Datum izmjene", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true },
        { field: "isActive", header: "Aktivan", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Aktivna", falseText: "Neaktivna", pairWith: "isDraft" },
        { field: "isDraft", header: "Skica", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Skica", falseText: "Bez skice" },
        { field: "version", header: "Verzija", bodyType: "number", filterType: "numeric", sortable: true, prefix: "v" },
        { field: "imageUrl", header: "Slika", bodyType: "file", icon: "image" },
        { field: "categoryNames", header: "Kategorije", bodyType: "modal", filterType: "cats", filterMode: "inArr", icon: "sell" },
    ]

    private _pageRequest = signal<TableLazyLoadEvent | undefined>(undefined)

    private _allCategories = toSignal(
        inject(CategoryService)
            .GetAllCached()
            .pipe(catchError(() => of([] as Category[])))
    )

    private _categories = computed(() => (this._allCategories() ?? []).filter(c => c.type === CategoryTypes.Benefit))

    benefits = httpResource<PagedBenefit>(() => {
        const event = this._pageRequest()
        const categories = this._allCategories()
        if (!event || categories === undefined) return undefined

        const resolveCats = (names: string[]): ColumnFilter | null => {
            const ids = this._categories()
                .filter(c => names.includes(c.names?.["hr"]))
                .map(c => c.masterId)
            return ids.length ? { field: "categoryIds", value: ids.join(","), matchMode: "in" } : null
        }

        return this._benefitsService.PagedRequest(toPagedParams(event, this.cols, resolveCats))
    })

    tableValues = computed(() =>
        (this.benefits.value()?.items ?? []).map(benefit => ({
            ...benefit,
            createdDate: new Date(benefit.createdDate),
            modifiedDate: new Date(benefit.modifiedDate),
        }))
    )

    totalRecords = computed(() => this.benefits.value()?.totalCount ?? 0)
    loading = computed(() => this.benefits.isLoading())

    refresh = () => this.benefits.reload()

    constructor() {
        effect(() => {
            if (!this.benefits.error()) return
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Došlo je do pogreške prilikom dohvata benefita.",
                life: 10000,
            })
        })
    }

    loadPage(event: TableLazyLoadEvent): void {
        setTimeout(() => this._pageRequest.set(event))
    }

    add(): void {
        this._router.navigate(["/benefits/add"])
    }

    edit(masterId: string): void {
        this._router.navigate(["/benefits/edit", masterId])
    }
}
